import { YesOrNo } from "./YesOrNo";

/** Confirmation / update / cancel email behavior in Reserve misc settings. */
export const RESERVE_MISC_EMAIL_NOTIFY_OPTIONS = [
  "Never",
  "Prompt Host",
  "Always",
] as const;

export type ReserveMiscEmailNotifyOption =
  (typeof RESERVE_MISC_EMAIL_NOTIFY_OPTIONS)[number];

/** Default outbound communication channel preference. */
export const RESERVE_MISC_DEFAULT_COMMUNICATION_PREFERENCE_OPTIONS = [
  "None",
  "Manual",
  "Sms",
  "VoiceCall",
  "Email",
] as const;

export type ReserveMiscDefaultCommunicationPreferenceOption =
  (typeof RESERVE_MISC_DEFAULT_COMMUNICATION_PREFERENCE_OPTIONS)[number];

const emailNotifySet = new Set<string>(RESERVE_MISC_EMAIL_NOTIFY_OPTIONS);
const commPrefSet = new Set<string>(
  RESERVE_MISC_DEFAULT_COMMUNICATION_PREFERENCE_OPTIONS,
);

/** Maps stored yes/no (any casing) to {@link YesOrNo} labels. */
export function normalizeReserveMiscYesNo(value: unknown): string {
  const s = String(value ?? "").trim();
  if (!s) return YesOrNo[0];
  const lower = s.toLowerCase();
  if (lower === "yes" || lower === "y" || lower === "true") return "Yes";
  if (lower === "no" || lower === "n" || lower === "false") return "No";
  if ((YesOrNo as readonly string[]).includes(s)) return s;
  return YesOrNo[0];
}

export function normalizeReserveMiscEmailNotify(value: unknown): string {
  const s = String(value ?? "").trim();
  if (emailNotifySet.has(s)) return s;
  return "Always";
}

export function normalizeReserveMiscCommunicationPreference(
  value: unknown,
): string {
  const s = String(value ?? "").trim();
  if (commPrefSet.has(s)) return s;
  if (s === "Email/Phone") return "Email";
  return "None";
}

/** Stored field keys on each misc row (excluding `id`, `Code`, `Name` for DND). */
export const RESERVE_MISC_SETTING_KEYS = [
  "Guest waiver form required",
  "Confirmation Email",
  "Update Email",
  "Cancel Email",
  "Allow to book/edit reservations for past date/time",
  "Allow to edit Checked-In Reservation",
  "No. of past days to view/edit past reservation",
  "Default Communication Preference",
] as const;

export type ReserveMiscSettingKey = (typeof RESERVE_MISC_SETTING_KEYS)[number];

export const RESERVE_MISC_DEFAULT_SETTINGS: Record<
  ReserveMiscSettingKey,
  string
> = {
  "Guest waiver form required": "Yes",
  "Confirmation Email": "Always",
  "Update Email": "Always",
  "Cancel Email": "Always",
  "Allow to book/edit reservations for past date/time": "Yes",
  "Allow to edit Checked-In Reservation": "Yes",
  "No. of past days to view/edit past reservation": "7",
  "Default Communication Preference": "Email",
};

const isYesNoSettingKey = (k: string) =>
  k === "Guest waiver form required" ||
  k === "Allow to book/edit reservations for past date/time" ||
  k === "Allow to edit Checked-In Reservation";

const isEmailNotifySettingKey = (k: string) =>
  k === "Confirmation Email" || k === "Update Email" || k === "Cancel Email";

/** Normalizes the eight setting fields from partial or legacy raw data. */
export function normalizeReserveMiscSettings(
  raw: Record<string, unknown> | null | undefined,
): Record<ReserveMiscSettingKey, string> {
  const out = { ...RESERVE_MISC_DEFAULT_SETTINGS };
  if (!raw) return out;
  for (const k of RESERVE_MISC_SETTING_KEYS) {
    if (raw[k] == null) continue;
    if (isYesNoSettingKey(k)) {
      out[k] = normalizeReserveMiscYesNo(raw[k]);
    } else if (isEmailNotifySettingKey(k)) {
      out[k] = normalizeReserveMiscEmailNotify(raw[k]);
    } else if (k === "Default Communication Preference") {
      out[k] = normalizeReserveMiscCommunicationPreference(raw[k]);
    } else {
      out[k] = String(raw[k]);
    }
  }
  return out;
}

/** One DND row from API / sample (drops unknown keys, keeps id/Code/Name). */
export function reserveMiscRowFromUnknown(
  row: unknown,
): Record<string, unknown> {
  if (!row || typeof row !== "object") {
    return {
      id: 1,
      Code: "",
      Name: "",
      ...RESERVE_MISC_DEFAULT_SETTINGS,
    };
  }
  const r = row as Record<string, unknown>;
  const idRaw = r.id;
  const id =
    typeof idRaw === "number" && !Number.isNaN(idRaw)
      ? idRaw
      : typeof idRaw === "string" &&
          idRaw.trim() !== "" &&
          Number.isFinite(Number(idRaw))
        ? Number(idRaw)
        : 1;
  const code = r.Code != null ? String(r.Code) : "";
  const name = r.Name != null ? String(r.Name) : "";
  return {
    id,
    Code: code,
    Name: name,
    ...normalizeReserveMiscSettings(r),
  };
}

/** Legacy payload was a single flat object without Code/Name — wrap as one row. */
export function reserveMiscLegacyFlatObjectToRow(
  o: Record<string, unknown>,
): Record<string, unknown> {
  return {
    id: 1,
    Code: "DEFAULT",
    Name: "Imported misc settings",
    ...normalizeReserveMiscSettings(o),
  };
}
